import React from 'react';
import type { IHeaderParams } from '@ag-grid-community/core';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { LayoutItem } from '../Layout/LayoutItem';
import { Layout } from '../Layout/Layout';
import { IconButton } from '../../element/Button/IconButton';
import { ContextMenu } from '../../element/ContextMenu/ContextMenu';

import type { SortStructure, SortType } from './data-grid.types';
import type { DataGridHeaderProps } from './DataGridHeader';

const sortIconMap = {
  asc: 'sort-ascending',
  desc: 'sort-descending',
} as const;

const sortButtonWrapperStyle = { display: 'inline-block' };

const getNextSort = ({
  currentSort,
  enableSorting,
  sortingOrder,
}: {
  currentSort?: SortType;
  enableSorting: boolean;
  sortingOrder?: Array<SortType | null>;
}): SortType | null => {
  if (!enableSorting) {
    return null;
  }

  if (!sortingOrder) {
    return null;
  }

  if (!currentSort) {
    return sortingOrder[0] ?? null;
  }
  const currentSortIndex = sortingOrder.indexOf(currentSort);
  return currentSortIndex + 1 >= sortingOrder.length
    ? (sortingOrder[0] ?? null)
    : (sortingOrder[currentSortIndex + 1] ?? null);
};

export type RawDataGridHeaderProps<T = unknown> = IHeaderParams &
  DataGridHeaderProps<T> & {
    onSortChange: (changeSort: SortStructure) => void;
    sorting: SortStructure;
  };

export const RawDataGridHeader = <T,>({
  enableSorting,
  sorting,
  sortingOrder,
  displayName,
  FiltersRender,
  onSortChange,
  column,
  name,
}: RawDataGridHeaderProps<T>) => {
  const currentSort = sorting[name];
  const sortIcon = currentSort && sortIconMap[currentSort];

  let sort = null;
  if (enableSorting) {
    sort = (
      <div style={sortButtonWrapperStyle}>
        <IconButton
          name="column-sort-button"
          icon={sortIcon || 'sort'}
          onClick={() => {
            const nextSort = getNextSort({
              currentSort,
              enableSorting,
              sortingOrder,
            });
            const columnId = column.getColId();
            onSortChange({ [columnId]: nextSort });
          }}
          pattern="secondary"
          variation="minimal"
        />
      </div>
    );
  }

  let renderedDisplay: React.JSX.Element | string = displayName;
  if (typeof displayName === 'function') {
    const displayFunction: () => React.JSX.Element = displayName;
    renderedDisplay = displayFunction();
  }
  return (
    <div className={AttrsHelper.formatClassname('custom-header')}>
      {!FiltersRender && !sort ? (
        renderedDisplay
      ) : (
        <Layout horizontalGutter="8px" position="middle" name={name}>
          <LayoutItem fluid name="column-display-name">
            {renderedDisplay}
          </LayoutItem>
          {!!FiltersRender && (
            <LayoutItem>
              <ContextMenu
                name="data-grid-filters"
                fixed
                anchor={
                  <IconButton
                    icon="filter"
                    pattern="secondary"
                    variation="minimal"
                  />
                }
              >
                {FiltersRender()}
              </ContextMenu>
            </LayoutItem>
          )}
          {!!sort && <LayoutItem>{sort}</LayoutItem>}
        </Layout>
      )}
    </div>
  );
};
